import {
  type HttpClient,
  type HttpClientRequestOptions,
} from '@service-initiation/shared-clients-http';
import { buildUrl } from '@service-initiation/shared-utils-helpers';

import {
  type CreateAppointmentIntentRequest,
  type CreateAppointmentIntentResponse,
  type CreateAppointmentRequest,
  type CreateAppointmentServerResponse,
  type CreatePaymentIntentRequest,
  type CreatePaymentIntentResponse,
  type DeleteAppointmentIntentRequest,
  type DeleteAppointmentIntentResponse,
  type GetAppointmentSettingsRequest,
  type GetAppointmentSettingsResponse,
  type GetBookingWindowsRequest,
  type GetBookingWindowsServerResponse,
  type GetDeviceTypeOptionsRequest,
  type GetDeviceTypeOptionsResponse,
  type GetDeviceTypesRequest,
  type GetDeviceTypesResponse,
  type LocateStoresRequest,
  type LocateStoresResponse,
  type RemoteTechService,
  type RepairPriceRequest,
  type RepairPriceResponse,
  type ServiceOptionsRequest,
  type ServiceOptionsResponse,
  type UpdateAppointmentRequest,
} from './types';

export type TechCareServicesClientOptions = {
  baseUrl?: string;
  httpClient: HttpClient;
};

export class TechCareServicesClient {
  private readonly baseUrl: TechCareServicesClientOptions['baseUrl'];
  private readonly httpClient: TechCareServicesClientOptions['httpClient'];

  constructor({
    baseUrl = process.env['UBIF_API_GATEWAY_URL'],
    httpClient,
  }: TechCareServicesClientOptions) {
    this.baseUrl = baseUrl;
    this.httpClient = httpClient;
  }

  public async cancelAppointment(
    leadId: number,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        leadId: leadId.toString(),
      },
      path: 'cancel-appointment',
    });

    return await this.httpClient.delete(url, options);
  }

  public async createAppointment(
    request: CreateAppointmentRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({ baseUrl: this.baseUrl, path: '/appointments' });

    return await this.httpClient.post<
      CreateAppointmentRequest,
      { data: CreateAppointmentServerResponse }
    >(url, request, options);
  }

  public async createAppointmentIntent(
    request: CreateAppointmentIntentRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      path: '/appointment-intent',
    });

    return await this.httpClient.post<
      CreateAppointmentIntentRequest,
      CreateAppointmentIntentResponse
    >(url, request, options);
  }

  public async createPaymentIntent(
    request: CreatePaymentIntentRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      path: '/create-payment-intent',
    });

    return await this.httpClient.post<
      CreatePaymentIntentRequest,
      CreatePaymentIntentResponse
    >(url, request, options);
  }

  public async deleteAppointmentIntent(
    request: DeleteAppointmentIntentRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      path: '/appointment-intent',
    });

    return await this.httpClient.post<
      DeleteAppointmentIntentRequest,
      DeleteAppointmentIntentResponse
    >(url, request, options);
  }

  public async getAppointmentSettings(
    request?: GetAppointmentSettingsRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        start_date: request?.startDate,
        store_id: request?.storeId.toString(),
      },
      path: '/appointment-settings',
    });

    return await this.httpClient.get<GetAppointmentSettingsResponse>(
      url,
      options
    );
  }

  public async getBookingWindows(
    request: GetBookingWindowsRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({ baseUrl: this.baseUrl, path: '/booking-windows' });

    return await this.httpClient.post<
      GetBookingWindowsRequest,
      GetBookingWindowsServerResponse
    >(url, request, options);
  }

  public async getDeviceTypeOptions(
    request: GetDeviceTypeOptionsRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        country_id: request.countryId?.toString(),
        device_repair_id: request.deviceRepairId.toString(),
        partner_id: request.partnerId?.toString(),
      },
      path: '/device-type-options',
    });

    return await this.httpClient.get<GetDeviceTypeOptionsResponse>(
      url,
      options
    );
  }

  public async getDeviceTypes(
    request?: GetDeviceTypesRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        device_type_id: request?.deviceTypeId?.toString(),
        include_extended: request?.includeExtended?.toString(),
        include_full: request?.includeFull?.toString(),
        include_repairs: request?.includeRepairs?.toString(),
      },
      path: '/device-types',
    });

    return await this.httpClient.get<GetDeviceTypesResponse>(url, options);
  }

  public async getRepairPrice(
    request: RepairPriceRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({ baseUrl: this.baseUrl, path: '/repair-price' });

    return await this.httpClient.post<RepairPriceRequest, RepairPriceResponse>(
      url,
      request
    );
  }
  public async getServiceOptions(
    request: ServiceOptionsRequest,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        device_repair_id: request.deviceRepairId.toString(),
        device_type_option_ids: `[${request.device_type_option_ids?.join(
          ','
        )}]`,
        postal_code: request.postal_code?.toString(),
      },
      path: '/service-options',
    });

    return await this.httpClient.get<ServiceOptionsResponse<RemoteTechService>>(
      url,
      options
    );
  }
  public async locateStores(
    request: LocateStoresRequest,
    options?: HttpClientRequestOptions
  ) {
    const endpoint =
      request?.next_available_appointment === true
        ? '/locate-stores-with-next-available-appointment'
        : '/locate-stores';

    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: request,
      path: endpoint,
    });

    return await this.httpClient.get<LocateStoresResponse>(url, options);
  }

  public async updateAppointment(
    request: UpdateAppointmentRequest,
    updatedLeadId: number,
    options?: HttpClientRequestOptions
  ) {
    const url = buildUrl({
      baseUrl: this.baseUrl,
      params: {
        leadId: updatedLeadId.toString(),
      },
      path: 'update-appointment',
    });

    return await this.httpClient.put<UpdateAppointmentRequest, void>(
      url,
      request,
      options
    );
  }
}
